import { useEffect, useState } from "react";
import type { ReactElement } from "react";
import type { TicketKarte, TicketOwnerCandidates } from "@kundeninfo/api-client";
import { Auswahlmenue } from "@kundeninfo/ui/me-design";
import { personOption } from "./Avatar.js";
import { HIERARCHY_TEXT } from "./hierarchyTexts.js";
import { ownerOptionData, ownerValue } from "./hierarchy.js";
import type { TicketsClient } from "./ticketsClient.js";

/**
 * DAS FELD „EIGENTÜMER" (Spec Teilaufgaben R7, R20, Festlegung 9 des Plans): EIN `Auswahlmenue` mit „System"
 * (Sonderoption `niemand` mit Zusatz), Personen (Avatar, „ich" oben) und Teams (Kürzel als Avatar). Wählbar ist, was
 * `owner-candidates` liefert — am Ticket (`ticketId`) oder beim Anlegen (`teamId`); der aktuelle Eigentümer bleibt
 * wählbar, auch wenn er nicht mehr in der Liste steht. `kompakt` (Vorgabe: ja, der Kopf des Steckbriefs trägt die
 * Beschriftung selbst) blendet die Beschriftung aus; ein Formular mit sichtbaren Beschriftungen setzt `kompakt: false`.
 */
export function OwnerField(p: {
  client: TicketsClient; ticketId?: string; teamId?: string | null; wert: string; aktuell: TicketKarte["owner"] | null;
  ichId: string; gesperrt: boolean; fehler?: string; breite?: string; kompakt?: boolean;
  onGeaendert: (wert: string) => void;
}): ReactElement {
  const [k, setK] = useState<TicketOwnerCandidates>({ users: [], teams: [] });
  const ticketId = p.ticketId;
  const teamId = p.teamId ?? null;
  useEffect(() => {
    let aktiv = true;
    const q = ticketId !== undefined ? { ticket: ticketId } : teamId === null ? {} : { team: teamId };
    p.client.listOwnerCandidates(q).then((r) => { if (aktiv) setK(r); }).catch(() => { if (aktiv) setK({ users: [], teams: [] }); });
    return () => { aktiv = false; };
  }, [p.client, ticketId, teamId]);
  const a = p.aktuell;
  const leute = a?.kind === "USER" && a.user !== null && !k.users.some((u) => u.id === a.user!.id) ? [a.user, ...k.users] : k.users;
  const teams = a?.kind === "TEAM" && a.team !== null && !k.teams.some((t) => t.id === a.team!.id) ? [a.team, ...k.teams] : k.teams;
  const ich = leute.find((u) => u.id === p.ichId) ?? null;
  return (
    <Auswahlmenue kompakt={p.kompakt !== false} {...(p.breite === undefined ? {} : { breite: p.breite })}
      beschriftung={HIERARCHY_TEXT.owner} wert={p.wert} gesperrt={p.gesperrt} {...(p.fehler === undefined ? {} : { fehler: p.fehler })}
      onGeaendert={(e) => p.onGeaendert(e.wert)}>
      <option value="" data-art="niemand" data-zusatz={HIERARCHY_TEXT.systemNote}>{HIERARCHY_TEXT.system}</option>
      {ich !== null && <option value={`user:${ich.id}`} data-art="ich" {...personOption(ich)}>{ich.name}</option>}
      {leute.filter((u) => u.id !== p.ichId).map((u) => (
        <option key={u.id} value={`user:${u.id}`} {...personOption(u)}>{u.name}</option>
      ))}
      {teams.map((t) => <option key={t.id} value={`team:${t.id}`} {...ownerOptionData(t)}>{`${t.shortCode} ${t.name}`}</option>)}
    </Auswahlmenue>
  );
}

export { ownerValue };
